/**
 * Saved Contact contract — shared wire shape for the user-defined "saved
 * contacts" library surfaced in web at `/saved-data` and on mobile via
 * Settings → Library → Saved Contacts.
 *
 * Web endpoints live at `/api/saved-contacts` and `/api/saved-contacts/[id]`.
 * The mobile mirror at `/api/mobile/v1/account/saved-contacts` consumes
 * these schemas via `safeParseOrWarn`.
 *
 * Prisma model `SavedContact` columns:
 *   id, userId, contactType, name, company?, phone?, email?, notes?
 *
 * The `contactType` enum mirrors the web route's `contactTypeEnum`.
 */
import { z } from "zod";

export const SavedContactTypeSchema = z.enum([
  "client",
  "principal_contractor",
  "principal_designer",
  "architect",
  "structural_engineer",
  "emergency",
]);
export type SavedContactType = z.infer<typeof SavedContactTypeSchema>;

export const SavedContactSchema = z.object({
  id: z.string(),
  contactType: SavedContactTypeSchema,
  name: z.string(),
  company: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  notes: z.string().nullable(),
});
export type SavedContact = z.infer<typeof SavedContactSchema>;

// Phone/email validation mirrors the web `ContactForm` regex constraints.
// Both fields are optional but if supplied must be plausibly shaped.
const PHONE_REGEX = /^[\d\s+\-()]{7,20}$/;

export const SavedContactCreateSchema = z.object({
  contactType: SavedContactTypeSchema,
  name: z.string().min(1).max(200),
  company: z.string().max(200).optional(),
  phone: z
    .string()
    .max(40)
    .regex(PHONE_REGEX, "Invalid phone number")
    .optional()
    .or(z.literal("")),
  email: z.string().max(200).email().optional().or(z.literal("")),
  notes: z.string().max(2000).optional(),
});
export type SavedContactCreate = z.infer<typeof SavedContactCreateSchema>;

export const SavedContactUpdateSchema = z.object({
  contactType: SavedContactTypeSchema.optional(),
  name: z.string().min(1).max(200).optional(),
  company: z.string().max(200).nullable().optional(),
  phone: z
    .string()
    .max(40)
    .regex(PHONE_REGEX, "Invalid phone number")
    .nullable()
    .optional()
    .or(z.literal("")),
  email: z
    .string()
    .max(200)
    .email()
    .nullable()
    .optional()
    .or(z.literal("")),
  notes: z.string().max(2000).nullable().optional(),
});
export type SavedContactUpdate = z.infer<typeof SavedContactUpdateSchema>;
